import React, { useState, useEffect } from 'react';
import {
  string,
  number,
  bool,
  func,
  shape as shapeType,
} from 'prop-types';
import {
  params,
  arrayOf as arrayOfType,
  number as numberType,
  shape,
  string as stringType,
  useDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import {
  Drawer,
  DrawerHeader,
} from '@one-thd/sui-atomic-components';
import { AddToQuoteFormDrawer } from './AddToQuoteFormDrawer';
import { CreateNewQuoteDrawer } from './CreateNewQuoteDrawer';
import { PriceChangeWarningDrawer } from './PriceChangeWarningDrawer';
import { SuccessDrawer } from './SuccessDrawer';
import { ErrorDrawer } from './ErrorDrawer';
import { SignInDrawer } from './SignInDrawer';
import { DrawerSkeleton } from '../shared/DrawerSkeleton';

import {
  GENERIC_ERROR,
  DEFAULT_SELECTED_QUOTE,
  QUOTE_ACTIVE_STATUS,
  QUOTE_PENDING_STATUS
} from '../shared/Constants';

const AddToQuoteDrawer = ({
  itemId,
  quantity,
  customerInfo,
  localStoreId,
  fulfillmentMethod,
  fulfillmentLocation,
  price,
  drawerShowing,
  setDrawerShowing
}) => {
  const [quotesPageSize, setQuotesPageSize] = useState(20);
  const [selectedQuote, setSelectedQuote] = useState(DEFAULT_SELECTED_QUOTE);
  const [error, setError] = useState(null);
  const [quoteName, setQuoteName] = useState('');
  const [successful, setSuccess] = useState(false); // used because of refetch causing loading
  const [quoteList, setQuoteList] = useState([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [addToQuoteFormDrawer, setAddToQuoteFormDrawer] = useState(false);
  const [createNewQuoteDrawer, setCreateNewQuoteDrawer] = useState(false);
  const [priceChangeWarningDrawer, setPriceChangeWarningDrawer] = useState(false);
  const [errorDrawer, setErrorDrawer] = useState(false);
  const [signInDrawer, setSignInDrawer] = useState(false);

  const { svocId, userId } = customerInfo;

  const variables = {
    svocId,
    pageSize: quotesPageSize,
    userId
  };

  /**
   * This component will be consumed in different places, like buybox on PIP,
   * and product pod in BuyItAgain and Lists. There are differences
   * in the `QueryProvider` configuration between these places that change
   * `fetchPolicy`, so we need to set one explicity that works for us no matter
   * where we are.
   *
   * For example, when on a PLP-like page, the `QueryProvider` will typically be
   * configured with dataSource prop equal to `searchNav`, which will override
   * the default `fetchPolicy` from `cache-first` to `cache-only`. This does
   * not work for our `getQuoteList` call below since we need to make the
   * call at least once to get data, hence the explicit fetchPolicy=cache-first
   * to check cache first, then make a network call if necessary.
   */

  const {
    data: quoteData,
    loading: quoteLoading,
    refetch,
    error: quoteError,
    variables: respvar
  } = useDataModel('getQuoteList', {
    variables,
    fetchPolicy: 'cache-first',
  });

  const { totalNbrOfQuotes } = quoteData?.getQuoteList || {};

  const isAccessTypeValid = (accessType) => {
    // hide if accessType on 1 or 2, show if null or not 1 or 2
    return !accessType || (accessType && (accessType !== 1 && accessType !== 2));
  };

  const statusIsValid = (status) => {
    const upperCased = status.toUpperCase();
    return upperCased === QUOTE_ACTIVE_STATUS || upperCased === QUOTE_PENDING_STATUS;
  };

  const shouldShowItem = ({ status, accessType }) => {
    return statusIsValid(status) && isAccessTypeValid(accessType);
  };

  useEffect(() => {
    if (quotesPageSize > 20) {
      refetch();
    }
    const quotes = quoteData?.getQuoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    setQuoteList(filteredQuotes);
    const index = quotes.length;
    if (quotes[index - 1]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [quotesPageSize, quoteData]);

  useEffect(() => {
    const quotes = quoteData?.getQuoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    if (drawerShowing && !quoteList.length && filteredQuotes.length) {
      setQuoteList(filteredQuotes);
    }
    const index = quotes.length - 1;
    if (quotes[index]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [drawerShowing, quoteList, quoteData]);

  useEffect(() => {
    if (quoteError && /AUTH/.test(quoteError.message)) {
      setSignInDrawer(true);
    }
    if (!quoteLoading && quoteError) {
      setErrorDrawer(true);
    }
    if (drawerShowing && quoteData && !quoteLoading && (!successful && !quoteError)) {
      if (totalNbrOfQuotes > 0) {
        setAddToQuoteFormDrawer(true);
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.addToQuoteOverlay');
      } else if (totalNbrOfQuotes === 0) {
        setCreateNewQuoteDrawer(true);
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.createAQuoteOverlay');
      }
    }
  }, [quoteData, quoteLoading, quoteError, drawerShowing]);

  const getErrorMessage = () => {
    if (!error) return '';
    const { message, graphQLErrors } = error;

    // check is API error message
    if (!message || message === 'null') {
      if (graphQLErrors.length) {
        const { extensions } = graphQLErrors[0];
        const { errorData } = extensions;
        if (errorData.length) {
          const { errors } = errorData[0];
          if (errors.length) return errors[0].description;
        }
      }
    }

    // Check if generic from server without helpful info
    if (/(50|51|40|41)/.test(message)) return GENERIC_ERROR;

    // Otherwise return the message
    return error.message;
  };

  const closeDrawer = () => {
    setDrawerShowing(false);
    setAddToQuoteFormDrawer(false);
    setCreateNewQuoteDrawer(false);
    setErrorDrawer(false);
    setSuccess(false);
    setPriceChangeWarningDrawer(false);
    setSignInDrawer(false);
    setQuoteName('');
    setSelectedQuote(DEFAULT_SELECTED_QUOTE);
  };

  return (
    <Drawer open={drawerShowing} onClose={closeDrawer}>
      <DrawerHeader
        title="Add to Quote"
        onClose={closeDrawer}
      />
      <div className="sui-flex sui-flex-col sui-min-h-screen">
        {quoteLoading && (
          <DrawerSkeleton />
        )}
        {createNewQuoteDrawer && !quoteLoading && (
          <CreateNewQuoteDrawer
            itemId={itemId}
            quantity={quantity}
            customerInfo={customerInfo}
            price={price}
            fulfillmentMethod={fulfillmentMethod}
            fulfillmentLocation={fulfillmentLocation}
            localStoreId={localStoreId}
            setError={setError}
            setQuoteName={setQuoteName}
            setSuccess={setSuccess}
            drawerShowing={drawerShowing}
            onClose={closeDrawer}
            setCreateNewQuoteDrawer={setCreateNewQuoteDrawer}
            setErrorDrawer={setErrorDrawer}
          />
        )}
        {addToQuoteFormDrawer && !quoteLoading && (
          <AddToQuoteFormDrawer
            itemId={itemId}
            quoteList={quoteList}
            selectedQuote={selectedQuote}
            setSelectedQuote={setSelectedQuote}
            quotesPageSize={quotesPageSize}
            setQuotesPageSize={setQuotesPageSize}
            showLoadMore={showLoadMore}
            drawerShowing={drawerShowing}
            onClose={closeDrawer}
            setAddToQuoteFormDrawer={setAddToQuoteFormDrawer}
            setCreateNewQuoteDrawer={setCreateNewQuoteDrawer}
            setPriceChangeWarningDrawer={setPriceChangeWarningDrawer}
          />
        )}
        {priceChangeWarningDrawer && (
          <PriceChangeWarningDrawer
            itemId={itemId}
            quantity={quantity}
            price={price}
            fulfillmentLocation={fulfillmentLocation}
            fulfillmentMethod={fulfillmentMethod}
            localStoreId={localStoreId}
            customerInfo={customerInfo}
            setQuoteName={setQuoteName}
            setError={setError}
            selectedQuote={selectedQuote}
            onClose={closeDrawer}
            setPriceChangeWarningDrawer={setPriceChangeWarningDrawer}
            setSuccess={setSuccess}
            setErrorDrawer={setErrorDrawer}
          />
        )}
        {successful && (
          <SuccessDrawer
            quoteName={quoteName}
            onClose={closeDrawer}
          />
        )}
        {signInDrawer && (
          <SignInDrawer onClose={closeDrawer} />
        )}
        {errorDrawer && (
          <ErrorDrawer
            message={getErrorMessage()}
            onClose={closeDrawer}
          />
        )}
      </div>
    </Drawer>
  );
};

AddToQuoteDrawer.propTypes = {
  itemId: string.isRequired,
  quantity: number.isRequired,
  customerInfo: shapeType({
    svocId: string.isRequired,
    userId: string.isRequired,
  }).isRequired,
  fulfillmentMethod: string.isRequired,
  fulfillmentLocation: string.isRequired,
  localStoreId: string.isRequired,
  price: number,
  drawerShowing: bool.isRequired,
  setDrawerShowing: func.isRequired
};

AddToQuoteDrawer.defaultProps = {
  price: null,
};

AddToQuoteDrawer.displayName = 'AddToQuoteDrawer';

AddToQuoteDrawer.dataModel = extend(
  {
    getQuoteList: params({
      svocId: stringType().isRequired(),
      userId: stringType(),
      pageNo: stringType(),
      pageSize: stringType(),
    }).shape({
      userId: stringType(),
      customerAccountId: stringType(),
      totalNbrOfQuotes: numberType({ int: true }),
      totalNbrOfPages: numberType({ int: true }),
      quotes: arrayOfType(
        shape({
          name: stringType(),
          status: stringType(),
          quoteId: stringType(),
          userId: stringType(),
          accessType: numberType(),
        }),
      ),
      errorData: arrayOfType(
        shape({
          errors: arrayOfType(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType(),
                }),
              }),
            }),
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType(),
        }),
      ),
    }),
  },
  CreateNewQuoteDrawer,
  PriceChangeWarningDrawer,
);

export { AddToQuoteDrawer };